import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize from 'rehype-sanitize';
import { Check, Copy, ChevronDown, ChevronUp, Bot, User } from 'lucide-react';
import { Button } from './ui/button';

// Code block with copy button and syntax highlighting
function CodeBlock({ inline, className, children, ...props }) {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  const code = String(children).replace(/\n$/, '');

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [code]);

  if (inline) {
    return (
      <code className="px-1.5 py-0.5 rounded bg-[var(--surface-card)] border border-[var(--surface-border)] text-sm font-mono text-[var(--accent-indigo)]" {...props}>
        {children}
      </code>
    );
  }

  return (
    <div className="relative group my-3 rounded-lg overflow-hidden border border-[var(--surface-border)] bg-[#0d1117]">
      {/* Language label and copy button */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#161b22] border-b border-[var(--surface-border)]">
        <span className="text-xs font-medium text-white/50 uppercase tracking-wider">
          {language || 'code'}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-white/50 hover:text-white hover:bg-white/10"
          onClick={handleCopy}
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-400" />
              <span className="text-emerald-400">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5 mr-1.5" />
              Copy
            </>
          )}
        </Button>
      </div>
      {/* Code content */}
      <pre className="p-4 overflow-x-auto text-sm leading-relaxed">
        <code className={`language-${language} text-white/90`} {...props}>
          {children}
        </code>
      </pre>
    </div>
  );
}

// Custom table component for responsive tables
function TableWrapper({ children }) {
  return (
    <div className="my-4 overflow-x-auto rounded-lg border border-[var(--surface-border)]">
      <table className="w-full border-collapse text-sm">
        {children}
      </table>
    </div>
  );
}

function TableHead({ children }) {
  return <thead className="bg-[var(--surface-card)]">{children}</thead>;
}

function TableRow({ children, isHeader }) {
  return (
    <tr className={`border-b border-[var(--surface-border)] last:border-0 ${!isHeader ? 'hover:bg-[var(--surface-card)]/50' : ''}`}>
      {children}
    </tr>
  );
}

function TableCell({ children, isHeader }) {
  const Component = isHeader ? 'th' : 'td';
  return (
    <Component className={`px-4 py-3 text-left ${isHeader ? 'font-semibold text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
      {children}
    </Component>
  );
}

// Markdown components configuration
const markdownComponents = {
  code: CodeBlock,
  table: TableWrapper,
  thead: TableHead,
  tr: ({ children, ...props }) => <TableRow {...props}>{children}</TableRow>,
  th: ({ children }) => <TableCell isHeader>{children}</TableCell>,
  td: ({ children }) => <TableCell>{children}</TableCell>,
  // Headings
  h1: ({ children }) => <h1 className="text-2xl font-bold mt-6 mb-4 text-[var(--text-primary)]">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-bold mt-5 mb-3 text-[var(--text-primary)]">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-semibold mt-4 mb-2 text-[var(--text-primary)]">{children}</h3>,
  h4: ({ children }) => <h4 className="text-base font-semibold mt-3 mb-2 text-[var(--text-primary)]">{children}</h4>,
  // Paragraphs and text
  p: ({ children }) => <p className="my-3 leading-7 text-[var(--text-primary)]">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-[var(--text-primary)]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => <del className="line-through opacity-60">{children}</del>,
  // Links
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-[var(--accent-indigo)] hover:underline">
      {children}
    </a>
  ),
  // Lists
  ul: ({ children }) => <ul className="my-3 ml-6 list-disc space-y-1.5">{children}</ul>,
  ol: ({ children }) => <ol className="my-3 ml-6 list-decimal space-y-1.5">{children}</ol>,
  li: ({ children, checked }) => {
    if (typeof checked === 'boolean') {
      return (
        <li className="flex items-start gap-2 list-none -ml-6">
          <input type="checkbox" checked={checked} readOnly className="mt-1.5 rounded border-[var(--surface-border)]" />
          <span className={checked ? 'line-through opacity-60' : ''}>{children}</span>
        </li>
      );
    }
    return <li className="leading-7">{children}</li>;
  },
  // Blockquotes
  blockquote: ({ children }) => (
    <blockquote className="my-4 pl-4 border-l-4 border-[var(--accent-indigo)]/30 italic text-[var(--text-secondary)]">
      {children}
    </blockquote>
  ),
  // Horizontal rule
  hr: () => <hr className="my-6 border-[var(--surface-border)]" />,
  // Images
  img: ({ src, alt }) => (
    <img src={src} alt={alt} className="my-4 rounded-lg max-w-full h-auto border border-[var(--surface-border)]" />
  ),
};

export default function ChatMessage({ message, isUser, isStreaming }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const content = message?.content || message?.text || '';
  const isLongMessage = content.length > 1500;

  return (
    <div className={`flex gap-4 py-5 px-4 ${isUser ? '' : 'bg-[var(--surface-card)]/30'}`} data-testid={`chat-message-${isUser ? 'user' : 'assistant'}`}>
      {/* Avatar */}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isUser ? 'bg-emerald-500/10' : 'bg-gradient-to-br from-purple-500 to-indigo-500'}`}>
        {isUser ? (
          <User className="w-5 h-5 text-emerald-400" />
        ) : (
          <Bot className="w-5 h-5 text-white" />
        )}
      </div>

      {/* Message content */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-sm font-medium ${isUser ? 'text-emerald-400' : 'text-[var(--accent-indigo)]'}`}>
            {isUser ? 'You' : 'BuilderShift AI'}
          </span>
          {isStreaming && (
            <span className="flex items-center gap-1 text-xs text-[var(--accent-indigo)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-indigo)] animate-pulse" />
              Generating...
            </span>
          )}
        </div>

        {/* Markdown content */}
        <div className={`prose prose-invert max-w-none ${!isExpanded && isLongMessage ? 'max-h-[400px] overflow-hidden relative' : ''}`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight, rehypeSanitize]}
            components={markdownComponents}
          >
            {content}
          </ReactMarkdown>
          
          {/* Gradient fade for collapsed long messages */}
          {!isExpanded && isLongMessage && (
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[var(--surface-bg)] to-transparent" />
          )}
        </div>

        {/* Expand/Collapse button for long messages */}
        {isLongMessage && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 text-xs text-[var(--accent-indigo)] hover:text-[var(--accent-indigo)]"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4 mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-1" />
                Show more
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
